import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import ButtonIcon from "../../../components/ButtonIcon/ButtonIcon";
import SearchInput from "../../../components/SearchInput/SearchInput";
import Table from "../../../components/Table";
import useCommonStore from "../../../stores/common";
import useShippingStore from "../../../stores/shipping";
import { fetchShippingList } from "../../../services/shipping";
import Pagination from "../../../components/Pagination/Pagination";
import { Copier } from "../../../../components/copier";
import CopyIcon from "../../../assets/svg/icons/common/copy";
import Filter from "../../../assets/svg/icons/common/Filter";
import RedDotBadge from "../../../assets/svg/icons/common/RedDotBadge";
import FilterDrawer from "../components/FilterDrawer";
import ShippingCard from "../components/ShippingCard";
import { getStatusBadgeContent } from "../utils/status";
import pageHeadingStyles from "../../../styles/PageHeader.module.scss";
import SkeletonLoader from "../../../components/skeleton-loader";
import rowsToFill from "../../../utils/rows-to-fill";
import styles from "./styles.module.scss";

const SEARCH_DEBOUNCE_MS = 500;

const List = () => {
  const debounceTimeoutRef = useRef(null);
  const cancelRef = useRef(null);

  const setFilterDrawerOpen = useCommonStore((state) => state.setFilterDrawerOpen);
  const showDetails = useCommonStore((state) => state.showDetails);
  const setShowDetails = useCommonStore((state) => state.setShowDetails);

  const shippingList = useShippingStore((state) => state.shippingList);
  const shippingListLoading = useShippingStore((state) => state.shippingListLoading);
  const setShippingListLoading = useShippingStore((state) => state.setShippingListLoading);
  const selectedShipping = useShippingStore((state) => state.selectedShipping);
  const setSelectedShipping = useShippingStore((state) => state.setSelectedShipping);
  const searchQuery = useShippingStore((state) => state.searchQuery);
  const setSearchQuery = useShippingStore((state) => state.setSearchQuery);
  const currentPage = useShippingStore((state) => state.currentPage);
  const setCurrentPage = useShippingStore((state) => state.setCurrentPage);
  const pageCount = useShippingStore((state) => state.pageCount);
  const totalItems = useShippingStore((state) => state.totalItems);
  const itemsPerPage = useShippingStore((state) => state.itemsPerPage);
  const officeFilter = useShippingStore(
    (state) => state.shippingFilterSearchQuery.office,
  );
  const orderDateFrom = useShippingStore(
    (state) => state.shippingFilterSearchQuery.orderDate.from,
  );
  const orderDateTo = useShippingStore(
    (state) => state.shippingFilterSearchQuery.orderDate.to,
  );
  const shipDateFrom = useShippingStore(
    (state) => state.shippingFilterSearchQuery.shipDate.from,
  );
  const shipDateTo = useShippingStore(
    (state) => state.shippingFilterSearchQuery.shipDate.to,
  );
  const typeFilter = useShippingStore(
    (state) => state.shippingFilterSearchQuery.type,
  );
  const courierFilter = useShippingStore(
    (state) => state.shippingFilterSearchQuery.courier,
  );
  const sortBy = useShippingStore(
    (state) => state.shippingFilterSearchQuery.sortBy,
  );
  const sortValue = useShippingStore(
    (state) => state.shippingFilterSearchQuery.sortValue,
  );
  const setShippingFilterSearchQuery = useShippingStore(
    (state) => state.setShippingFilterSearchQuery,
  );

  const hasOfficeSelected =
    typeof officeFilter === "string"
      ? officeFilter.trim() !== ""
      : Array.isArray(officeFilter) && officeFilter.length > 0;

  const activeFiltersCount =
    (hasOfficeSelected ? 1 : 0) +
    (typeFilter && typeFilter !== "All" ? 1 : 0) +
    (courierFilter && courierFilter !== "All" ? 1 : 0) +
    (orderDateFrom || orderDateTo ? 1 : 0) +
    (shipDateFrom || shipDateTo ? 1 : 0);

  const listEmptyMessage = !hasOfficeSelected && !shippingListLoading
    ? "Select a Kurv office in Filters to load shipments"
    : "No shipping records found";

  const [searchInput, setSearchInput] = useState(searchQuery);

  useEffect(() => {
    if (searchInput === searchQuery) {
      return undefined;
    }
    debounceTimeoutRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchInput, searchQuery, setSearchQuery, setCurrentPage]);

  const fetchData = useCallback(async () => {
    if (cancelRef.current) {
      cancelRef.current.cancel();
    }
    cancelRef.current = axios.CancelToken.source();

    try {
      setShippingListLoading(true);
      await fetchShippingList({}, cancelRef.current.token);
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      console.error("Error loading shipping list:", error);
    } finally {
      setShippingListLoading(false);
    }
  }, [setShippingListLoading]);

  useEffect(() => {
    if (!hasOfficeSelected) {
      useShippingStore.getState().setShippingList([]);
      useShippingStore.getState().setPageCount(1);
      useShippingStore.getState().setTotalItems(0);
      return;
    }
    fetchData();
  }, [
    fetchData,
    hasOfficeSelected,
    currentPage,
    itemsPerPage,
    searchQuery,
    officeFilter,
    typeFilter,
    courierFilter,
    orderDateFrom,
    orderDateTo,
    shipDateFrom,
    shipDateTo,
    sortBy,
    sortValue,
  ]);

  useEffect(() => {
    return () => {
      if (cancelRef.current) {
        cancelRef.current.cancel();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const onChangeSearchInput = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);

  const onRowClick = useCallback(
    (row) => {
      if (!selectedShipping || selectedShipping.key !== row.key) {
        setSelectedShipping(row);
      }
      setShowDetails(true);
    },
    [selectedShipping, setSelectedShipping, setShowDetails],
  );

  const handleSort = useCallback(
    (field) => {
      setShippingFilterSearchQuery({
        sortBy: field,
        sortValue: sortBy === field && sortValue === "DESC" ? "ASC" : "DESC",
        page: 1,
      });
      setCurrentPage(1);
    },
    [sortBy, sortValue, setShippingFilterSearchQuery, setCurrentPage],
  );

  const columns = useMemo(
    () => [
      {
        headerName: "DBA",
        field: "dba",
        width: "320px",
        maxWidth: "320px",
        allowSort: false,
        renderCell: (value) => (
          <div className={styles.dbaCell} title={value || undefined}>
            {value}
          </div>
        ),
      },
      {
        headerName: "MID",
        field: "mid",
        width: "220px",
        maxWidth: "220px",
        allowSort: true,
        sortBy: sortBy === "mid",
        sortColumn: sortValue,
        renderCell: (value) => (
          <div className={styles.midCell} title={value || undefined}>
            {value}
          </div>
        ),
      },
      {
        headerName: "TYPE",
        field: "type",
        width: "100px",
        allowSort: true,
        sortBy: sortBy === "type",
        sortColumn: sortValue,
        renderCell: (value) => (
          <div className={styles.typeCell}>{value}</div>
        ),
        hideCell: showDetails,
      },
      {
        headerName: "OFFICE",
        field: "office",
        width: "120px",
        allowSort: true,
        sortBy: sortBy === "office",
        sortColumn: sortValue,
        renderCell: (value) => (
          <div className={styles.officeCell}>{value}</div>
        ),
        hideCell: showDetails,
      },
      {
        headerName: "SHIP TO",
        field: "ship_to",
        width: "300px",
        maxWidth: "300px",
        allowSort: true,
        sortBy: sortBy === "ship_to",
        sortColumn: sortValue,
        renderCell: (value) => (
          <div className={styles.shipToCell} title={value || undefined}>
            {value}
          </div>
        ),
        hideCell: showDetails,
      },
      {
        headerName: "ORDER DATE",
        field: "order_date",
        width: "120px",
        allowSort: true,
        sortBy: sortBy === "order_date",
        sortColumn: sortValue,
        renderCell: (value) => (
          <div className={styles.dateCell}>{value}</div>
        ),
        hideCell: showDetails,
      },
      {
        headerName: "SHIP DATE",
        field: "ship_date",
        width: "120px",
        allowSort: true,
        sortBy: sortBy === "ship_date",
        sortColumn: sortValue,
        renderCell: (value) => (
          <div className={styles.dateCell}>{value}</div>
        ),
      },
      {
        headerName: "COURIER",
        field: "courier",
        width: "100px",
        allowSort: true,
        sortBy: sortBy === "courier",
        sortColumn: sortValue,
        renderCell: (value) => (
          <div className={styles.courierCell}>{value}</div>
        ),
        hideCell: showDetails,
      },
      {
        headerName: "PACKAGE TN",
        field: "package_tn",
        width: "180px",
        maxWidth: "180px",
        allowSort: false,
        renderCell: (value, row) => (
          <div className={styles.packageTnCell}>
            {row?.package_tracking_url && value ? (
              <a
                href={row.package_tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.trackingLink}
                title={value}
                onClick={(e) => e.stopPropagation()}
              >
                {value}
              </a>
            ) : (
              <span title={value || undefined}>{value}</span>
            )}
            {value && (
              <Copier text={value}>
                <button className={styles.copyButton} title="Copy tracking number">
                  <CopyIcon />
                </button>
              </Copier>
            )}
          </div>
        ),
        hideCell: showDetails,
      },
      {
        headerName: "CALL TAG TN",
        field: "call_tag_tn",
        width: "180px",
        maxWidth: "180px",
        allowSort: false,
        renderCell: (value) => (
          <div className={styles.callTagTnCell}>
            <span title={value || undefined}>{value}</span>
            {value && (
              <Copier text={value || ""}>
                <button className={styles.copyButton} title="Copy call tag tracking number">
                  <CopyIcon />
                </button>
              </Copier>
            )}
          </div>
        ),
        hideCell: showDetails,
      },
      {
        headerName: "STATUS",
        field: "status",
        width: "120px",
        allowSort: false,
        renderCell: (value) => {
          const badge = getStatusBadgeContent(value);
          return (
            <div className={`${styles.statusCell} ${styles[badge.variant]}`}>
              {badge.icon}
              <span>{badge.text}</span>
            </div>
          );
        },
      },
    ],
    [showDetails, sortBy, sortValue],
  );

  const tableData = useMemo(
    () =>
      shippingList.map((item, index) => ({
        key: item.id || index,
        ...item,
      })),
    [shippingList],
  );

  return (
    <>
      <div className={styles.mobile_header}>
        <div className={styles.mobile_filters}>
          <div className={styles.search_wrapper}>
            <SearchInput
              placeholder="Search shipping..."
              searchValue={searchInput}
              onChange={onChangeSearchInput}
            />
          </div>
          <ButtonIcon
            bgColor="#EDEDF4"
            onClick={() => setFilterDrawerOpen(true)}
            className={styles.filter_button}
          >
            <Filter />
            {activeFiltersCount > 0 && <RedDotBadge className={styles.filter_active} />}
          </ButtonIcon>
        </div>
      </div>

      <div className={`${pageHeadingStyles.page_heading} ${pageHeadingStyles.column}`}>
        <div className={pageHeadingStyles.title_contents}>
          <p>Shipment Tracking</p>
        </div>
        <div className={styles.search_input_container}>
          <SearchInput
            placeholder="Search"
            searchValue={searchInput}
            onChange={onChangeSearchInput}
          />
          <ButtonIcon
            bgColor="#EDEDF4"
            onClick={() => setFilterDrawerOpen(true)}
            className={styles.filter_button}
          >
            <Filter />
            {activeFiltersCount > 0 && <RedDotBadge />}
            Filter
            {activeFiltersCount > 0 ? `: ${activeFiltersCount}` : ""}
          </ButtonIcon>
        </div>
      </div>

      <div className={styles.card}>
        {shippingListLoading && (
          <div className={styles.mobile_cards_container}>
            {Array.from({ length: rowsToFill(window.innerHeight) }).map(
              (_, index) => (
                <div key={index} className={styles.skeleton_mobile_card}>
                  <div className={styles.skeleton_card_left} />
                  <div className={styles.skeleton_card_center}>
                    <SkeletonLoader
                      variant="text"
                      width="80%"
                      height="14px"
                      style={{ minHeight: "14px" }}
                    />
                  </div>
                  <div className={styles.skeleton_card_right}>
                    <SkeletonLoader
                      variant="circular"
                      width="16px"
                      height="16px"
                    />
                  </div>
                </div>
              ),
            )}
          </div>
        )}

        <div className={styles.desktop_table}>
          <Table
            layout="fixed"
            columns={columns}
            data={shippingListLoading ? [] : tableData}
            onRowClick={onRowClick}
            onSortClick={handleSort}
            selectedRowId={selectedShipping?.key}
            sortBy={sortBy}
            sortColumn={sortValue}
            emptyMessage={listEmptyMessage}
            className={styles.shipping_table}
            enableSkeleton
            dataLoading={shippingListLoading}
          />
        </div>

        {!shippingListLoading && shippingList.length === 0 && (
          <p className={styles.empty_state_mobile}>{listEmptyMessage}</p>
        )}

        {!shippingListLoading && shippingList.length > 0 && (
          <>
            <div className={styles.mobile_cards_container}>
              {tableData.map((shipment) => (
                <ShippingCard
                  key={shipment.key}
                  shipment={shipment}
                  onClick={() => onRowClick(shipment)}
                  isSelected={selectedShipping?.key === shipment.key}
                />
              ))}
            </div>

            <div className={styles.pagination_container}>
              <Pagination
                pageCount={pageCount}
                currentPage={currentPage - 1}
                onPageChange={({ selected }) => {
                  setCurrentPage(selected + 1);
                }}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
              />
            </div>
          </>
        )}
      </div>
      <FilterDrawer />
    </>
  );
};

export default List;
